import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";
import { ProtectedRoute } from "@/components/common/ProtectedRoute";
import { AppLayout } from "@/components/layout/AppLayout";
import { ViewAsBanner } from "@/components/layout/ViewAsBanner";
import { RootErrorBoundary } from "@/components/common/RootErrorBoundary";
import { ShellPlaceholder } from "@/pages/ShellPlaceholder";
import { KbPage } from "@/pages/kb/KbPage";
import { KnowledgeHomePage } from "@/pages/knowledge/KnowledgeHomePage";
import { KnowledgeSectionPage } from "@/pages/knowledge/KnowledgeSectionPage";
import { KnowledgeItemPage } from "@/pages/knowledge/KnowledgeItemPage";
import { KnowledgeEditorPage } from "@/pages/knowledge/KnowledgeEditorPage";
import { KnowledgeAdminPage } from "@/pages/knowledge/KnowledgeAdminPage";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { LoginPage } from "@/pages/LoginPage";
import { HomeQueuePage } from "@/pages/HomeQueuePage";
import { TasksListPage } from "@/pages/TasksListPage";
import { MeetingsQueuePage } from "@/pages/MeetingsQueuePage";
import { RequestsListPage } from "@/pages/RequestsListPage";
import { ContractsWorkspace } from "@/pages/contracts/ContractsWorkspace";
import { ArchiveReviewPage } from "@/pages/contracts/ArchiveReviewPage";
import { ContractDetailV2 } from "@/pages/contracts/ContractDetailV2";
import { ComplaintsListPage } from "@/pages/ComplaintsListPage";
import { ContactsListPage } from "@/pages/contacts/ContactsListPage";
import { ContactDetailPage } from "@/pages/contacts/ContactDetailPage";
import {
  ContactOverviewTab,
  ContactMethodsTab,
  ContactAccountsTab,
  ContactActivitiesTab,
  ContactContractsTab,
  ContactMergeTab,
} from "@/pages/contacts/ContactDetailTabs";
import { NeedsReviewQueuePage } from "@/pages/needs-review/NeedsReviewQueuePage";
import { SyncCenterPage } from "@/pages/SyncCenterPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { AdminCenterPage } from "@/pages/admin/AdminCenterPage";
import { AdminUsersPage } from "@/pages/admin/AdminUsersPage";
import { AdminStructurePage } from "@/pages/admin/AdminStructurePage";
import { AdminRolesPage } from "@/pages/admin/AdminRolesPage";
import { AdminRoutesPage } from "@/pages/admin/AdminRoutesPage";
import { AdminLegalEntitiesPage } from "@/pages/admin/AdminLegalEntitiesPage";
import { AdminSuppliersPage } from "@/pages/admin/AdminSuppliersPage";
import { AdminHealthPage } from "@/pages/admin/AdminHealthPage";
import { AdminAuditPage } from "@/pages/admin/AdminAuditPage";
import { AdminViewAsPage } from "@/pages/admin/AdminViewAsPage";
// A-Block — holding-first client routes + legacy redirects.
import { ClientsListPage } from "@/pages/clients/ClientsListPage";
import { Client360Page } from "@/pages/clients/Client360Page";
import { Client360Overview } from "@/pages/clients/Client360Overview";
import {
  ClientComplaintsTab,
  ClientContractsTab,
  ClientEntitiesTab,
} from "@/pages/clients/Client360Tabs";
import { ClientTimelineTab, ClientTasksTab } from "@/pages/clients/Client360TaskTabs";
import { ClientMaterialsTab } from "@/pages/clients/ClientMaterialsTab";
import { ClientContactsTab } from "@/pages/clients/Client360ContactsTab";
import { ClientStructureTab } from "@/pages/clients/ClientStructureTab";
import { OutletsListPage } from "@/pages/clients/OutletsListPage";
import { ContractDrawerShell } from "@/pages/clients/ContractDrawerShell";
import { ActivityDrawerShell } from "@/pages/clients/ActivityDrawerShell";
import { BusinessProfilePage } from "@/pages/clients/BusinessProfilePage";
import { ProfilePage } from "@/pages/clients/ProfilePage";
import { LegacyAccountRedirect } from "@/pages/legacy/LegacyAccountRedirect";
import { NotificationsPage } from "@/pages/NotificationsPage";

// react-router basename: strip trailing slash from vite BASE_URL ("/app/" → "/app", "/" → "").
const BASENAME = import.meta.env.BASE_URL.replace(/\/$/, "") || "/";

export default function App() {
  return (
    // D-044 S5 HOTFIX — ViewAsBanner на ROOT-рівні (НАД ProtectedRoute):
    // escape-hatch «Вийти» завжди видимий під час активної сесії, навіть якщо
    // /me/контент не завантажився (skeleton/crash). RootErrorBoundary гарантує,
    // що краш будь-якого екрану не ховає банер.
    <div className="flex h-screen flex-col">
      <ViewAsBanner />
      <div className="min-h-0 flex-1">
        <RootErrorBoundary>
          <BrowserRouter basename={BASENAME}>
            <Routes>
        {/* Public — no session required */}
        <Route path="/login" element={<LoginPage />} />

        {/* Everything else gated by ProtectedRoute */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/today" replace />} />
          {/* F6 Home Unified Action Queue is the home (superseded F1 HomePage — sub_5 cleanup). */}
          <Route path="/today" element={<HomeQueuePage />} />

          {/* ── A-Block: holding-first Client360 routes (Client360-first, child nested). ──
              AC-A0-01 all client routes nested under /clients/:holdingId.
              AC-A0-02 standalone synthetic client reuses the same route.
              AC-A0-03 NO root-level legal-entity route — LE only nested here. */}
          <Route path="/clients" element={<ClientsListPage />} />
          <Route path="/clients/:holdingId" element={<Client360Page />}>
            <Route index element={<Client360Overview />} />
            {/* Lazy tab panels (A2 compact+lazy) */}
            <Route path="legal-entities" element={<ClientEntitiesTab />} />
            <Route path="legal-entities/:accountId" element={<ClientEntitiesTab />} />
            <Route path="structure" element={<ClientStructureTab />} />
            <Route path="outlets" element={<OutletsListPage />} />
            <Route path="contacts" element={<ClientContactsTab />} />
            <Route path="contracts" element={<ClientContractsTab />} />
            <Route path="contracts/new" element={<ContractDrawerShell />} />
            <Route path="contracts/:contractId" element={<ContractDrawerShell />} />
            {/* 3A Task Core — Активності = read-only Timeline projection. */}
            <Route path="activities" element={<ClientTimelineTab />} />
            <Route path="activity/new" element={<ActivityDrawerShell />} />
            <Route path="activity/:activityId" element={<ActivityDrawerShell />} />
            {/* 3A Task Core — Задачі (open tasks, holding grain). */}
            <Route path="tasks" element={<ClientTasksTab />} />
            <Route path="complaints" element={<ClientComplaintsTab />} />
            {/* D-146 W2 — Матеріали (10-й таб, holding-grain). */}
            <Route path="materials" element={<ClientMaterialsTab />} />
            {/* A3 BusinessProfile Gate v2 Quick Form */}
            {/* Этап 6 — Профіль type-aware surface (scenario editing delegates to business-profile). */}
            <Route path="profile" element={<ProfilePage />} />
            <Route path="business-profile" element={<BusinessProfilePage />} />
          </Route>

          {/* ── A0_sub0 legacy account routes → holding anchor (HIGH-1 · AC-A0-05). ──
              No active membership → no-access page (NOT a random holding). */}
          <Route path="/accounts" element={<Navigate to="/clients" replace />} />
          <Route path="/accounts/:id" element={<LegacyAccountRedirect />} />
          <Route path="/account/:id" element={<LegacyAccountRedirect />} />

          <Route path="/holdings" element={<ShellPlaceholder />} />
          <Route path="/outlets" element={<ShellPlaceholder />} />
          {/* Phase 3.X-1B sub_1 — Contact LIST (was a crmold ShellPlaceholder bounce). */}
          <Route path="/contacts" element={<ContactsListPage />} />
          {/* Phase 3.X-1B sub_2 — Contact DETAIL (envelope + lazy tabs). */}
          <Route path="/contacts/:contactId" element={<ContactDetailPage />}>
            <Route index element={<ContactOverviewTab />} />
            <Route path="methods" element={<ContactMethodsTab />} />
            <Route path="accounts" element={<ContactAccountsTab />} />
            <Route path="activities" element={<ContactActivitiesTab />} />
            <Route path="contracts" element={<ContactContractsTab />} />
            <Route path="merge-status" element={<ContactMergeTab />} />
          </Route>
          {/* WS-2 (D-101 §4): єдиний шлях — workspace + detail v2; legacy F4
              стек і флаг custom_contract_view_enabled видалені. */}
          <Route path="/contracts" element={<ContractsWorkspace />} />
          {/* G3-S3 — Archive Review (visual ④) */}
          <Route path="/contracts/archive-review" element={<ArchiveReviewPage />} />
          <Route path="/contracts/:id" element={<ContractDetailV2 />} />
          <Route path="/complaints" element={<ComplaintsListPage />} />
          <Route path="/complaints/:id" element={<ComplaintsListPage />} />
          {/* D-055 S4 — Meetings Working Queue (was ShellPlaceholder). */}
          <Route path="/meetings" element={<MeetingsQueuePage />} />
          <Route path="/tasks" element={<TasksListPage />} />
          {/* D-043 S2 — registry deepLink adapters: /tasks/{id} → ?open drawer. */}
          <Route path="/tasks/:id" element={<TaskDeepLinkRedirect />} />
          {/* 3B — Internal Requests (department queue). */}
          <Route path="/requests" element={<RequestsListPage />} />
          <Route path="/requests/:id" element={<RequestDeepLinkRedirect />} />
          {/* D-043 S2 — Центр сповіщень (TZ §6.2). */}
          <Route path="/notifications" element={<NotificationsPage />} />
          {/* Phase 3.X-1B sub_3 — NeedsReview queue (was StubDialog→crmold bounce). */}
          <Route path="/needs-review" element={<NeedsReviewQueuePage />} />
          {/* AI-1 (D-105) — /kb = AI-помічник (D-147 S2 rename). */}
          <Route path="/kb" element={<KbPage />} />
          {/* D-147 S3 — «База знань» (Knowledge Library), reader-зріз. */}
          <Route path="/knowledge" element={<KnowledgeHomePage />} />
          <Route path="/knowledge/sections/:sectionId" element={<KnowledgeSectionPage />} />
          <Route path="/knowledge/items/:itemId" element={<KnowledgeItemPage />} />
          {/* D-147 S4 — редактор чернетки (editor+; publish звідси НЕ існує). */}
          <Route path="/knowledge/items/:itemId/edit" element={<KnowledgeEditorPage />} />
          {/* D-147 S5 — адмін розділів (KB_Admin / owner-и своїх). */}
          <Route path="/knowledge/admin" element={<KnowledgeAdminPage />} />
          {/* Sync Orchestration §11 — Admin Sync Center (status read + super_admin controls). */}
          <Route path="/sync-center" element={<SyncCenterPage />} />
          {/* D-044 S0 — Admin Center shell (admin-tier only; екрани S1-S5). */}
          <Route path="/admin" element={<AdminCenterPage />} />
          {/* D-044 S1 — #1 Users list + #2 Add-user wizard (?new=1). */}
          <Route path="/admin/users" element={<AdminUsersPage />} />
          <Route path="/admin/users/new" element={<AdminUsersNewRedirect />} />
          {/* D-044 S2 — #3 Org-tree + #4 Roles & access matrix. */}
          <Route path="/admin/structure" element={<AdminStructurePage />} />
          <Route path="/admin/roles" element={<AdminRolesPage />} />
          {/* D-044 S3 — #6 Departments & routes (absorb IR Admin drawer, AC-ADM-050). */}
          <Route path="/admin/routes" element={<AdminRoutesPage />} />
          {/* G2-S3 — Юрособи Kormax governance (D2=C). */}
          <Route path="/admin/legal-entities" element={<AdminLegalEntitiesPage />} />
          {/* D-133 Фаза A — довідник постачальників (admin/QueueAdmin). */}
          <Route path="/admin/suppliers" element={<AdminSuppliersPage />} />
          {/* D-044 S4 — #7 System Health (P3 cached) + #8 Audit Log (P8). */}
          <Route path="/admin/health" element={<AdminHealthPage />} />
          <Route path="/admin/audit" element={<AdminAuditPage />} />
          {/* D-044 S5 — #5 Access preview / view-as (server-side session, read-only). */}
          <Route path="/admin/view-as" element={<AdminViewAsPage />} />
          {/* Settings shell (TZ_Settings_Home v1.1). Always-on static route — no
              backend featureFlag. Nav/UserMenu exposure is gated by
              SETTINGS_ROUTE_ENABLED (lib/settingsConfig.ts) for one-line rollback. */}
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
          </BrowserRouter>
        </RootErrorBoundary>
      </div>
    </div>
  );
}

// D-044 S1 — /admin/users/new = список з відкритим wizard (?new=1).
function AdminUsersNewRedirect() {
  return <Navigate to="/admin/users?new=1" replace />;
}

// D-043 S2 — registry deepLinkPatterns are /tasks/{id} та /requests/{id}, але
// живі маршрути відкривають drawer через ?open=. Адаптери тримають deep-link
// контракт стабільним без зміни патернів у registry.
function TaskDeepLinkRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={id ? `/tasks?open=${id}` : "/tasks"} replace />;
}
function RequestDeepLinkRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={id ? `/requests?open=${id}` : "/requests"} replace />;
}
